// The takeoff canvas's right-hand dock: which panes show, in what order, in one column or two. A per-viewer VIEW setting
// (localStorage) — nothing is stored with the estimate, and two estimators on the same estimate can lay it out differently.

export const DOCK_PANELS = Object.freeze([
  { key: "keyplan", label: "Keyplan" },
  { key: "queue", label: "Review" },
  { key: "review", label: "Review · Walls" },
  { key: "routes", label: "Routes" },
  { key: "rooms", label: "Rooms" },
  { key: "conditions", label: "Conditions" },
  { key: "layers", label: "Layers" },
]);

const KEYS = DOCK_PANELS.map(p => p.key);
export const DOCK_LAYOUT_STORAGE_KEY = "cs.takeoff.dockPanels";

// The default (chosen by the estimators): two columns — Keyplan, Review, Review · Walls and Routes on the left, the working
// lists (Rooms, Conditions, Layers) on the right. The order above is the default order; column membership is below.
const DEFAULT_COLS = Object.freeze({ keyplan: 0, queue: 0, review: 0, routes: 0, rooms: 1, conditions: 1, layers: 1 });

export const DEFAULT_DOCK_LAYOUT = Object.freeze({ order: KEYS, hidden: [], cols: DEFAULT_COLS, twoCol: true, autoCollapse: false });

/** The dock's width (px) to start from: wide enough for two columns by default, the old narrow strip for one. */
export const DOCK_TWO_COL_W = 740;
export const DOCK_ONE_COL_W = 258;

/** Whatever was stored (or nothing) -> a complete, valid layout. A pane added to the app later shows up at the end. */
export function normalizeDockLayout(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const seen = new Set();
  const order = [];
  for (const k of Array.isArray(src.order) ? src.order : []) if (KEYS.includes(k) && !seen.has(k)) { seen.add(k); order.push(k); }
  for (const k of KEYS) if (!seen.has(k)) order.push(k);
  const hidden = [...new Set((Array.isArray(src.hidden) ? src.hidden : []).filter(k => KEYS.includes(k)))];
  const cols = {};
  for (const k of KEYS) cols[k] = src.cols && (src.cols[k] === 0 || src.cols[k] === 1) ? src.cols[k] : DEFAULT_COLS[k];
  // a layout saved before two columns existed has no twoCol key -> the default; an explicit false stays false
  // autoCollapse is opt-in: off (the original behaviour, every pane opens and closes on its own) unless the viewer ticks it
  return { order, hidden, cols, twoCol: src.twoCol === undefined ? DEFAULT_DOCK_LAYOUT.twoCol : !!src.twoCol, autoCollapse: src.autoCollapse === true };
}

export function readDockLayout(storage = typeof localStorage !== "undefined" ? localStorage : null) {
  try { return normalizeDockLayout(JSON.parse(storage?.getItem(DOCK_LAYOUT_STORAGE_KEY) || "null")); } catch { return normalizeDockLayout(null); }
}
/** Did the viewer ever choose one or two columns themselves? Then the window-size default must not override it. */
export function hasExplicitDockColumns(storage = typeof localStorage !== "undefined" ? localStorage : null) {
  try { const raw = JSON.parse(storage?.getItem(DOCK_LAYOUT_STORAGE_KEY) || "null"); return !!raw && typeof raw === "object" && raw.twoCol !== undefined; } catch { return false; }
}

/**
 * The layout actually shown for this window: the dock slides over the drawing in a narrow window and is never two columns
 * there; in a medium window it starts as ONE column unless the viewer chose two themselves; a wide window uses the saved layout.
 */
export function effectiveDockLayout(layout, dockMode, explicitColumns) {
  if (dockMode === "drawer" || (dockMode === "one" && !explicitColumns)) return layout.twoCol ? { ...layout, twoCol: false } : layout;
  return layout;
}

export function writeDockLayout(layout, storage = typeof localStorage !== "undefined" ? localStorage : null) {
  try { storage?.setItem(DOCK_LAYOUT_STORAGE_KEY, JSON.stringify(layout)); } catch { /* a view preference only */ }
}

/** Move `key` so it sits at `toIndex` of the order (the index it should end up at). */
export function moveInOrder(layout, key, toIndex) {
  const from = layout.order.indexOf(key);
  if (from < 0) return layout;
  const order = layout.order.filter(k => k !== key);
  order.splice(Math.max(0, Math.min(order.length, toIndex)), 0, key);
  return { ...layout, order };
}

/** Move `key` to just before `beforeKey` (drag and drop), or to the end when `beforeKey` is null. */
export function moveBefore(layout, key, beforeKey) {
  if (key === beforeKey) return layout;
  const rest = layout.order.filter(k => k !== key);
  const at = beforeKey == null ? rest.length : rest.indexOf(beforeKey);
  if (at < 0) return layout;
  rest.splice(at, 0, key);
  return { ...layout, order: rest };
}

export const nudge = (layout, key, dir) => moveInOrder(layout, key, layout.order.indexOf(key) + dir);

export const isHidden = (layout, key) => layout.hidden.includes(key);
export const setHidden = (layout, key, hide) => ({ ...layout, hidden: hide ? [...new Set([...layout.hidden, key])] : layout.hidden.filter(k => k !== key) });
export const setColumn = (layout, key, col) => ({ ...layout, cols: { ...layout.cols, [key]: col === 1 ? 1 : 0 } });
export const setTwoCol = (layout, on) => ({ ...layout, twoCol: !!on });
export const setAutoCollapse = (layout, on) => ({ ...layout, autoCollapse: !!on });

/** Panes that never take part in the one-open-at-a-time rule: the Keyplan is a fixed-size minimap, not a working list. */
export const ACCORDION_EXEMPT = Object.freeze(["keyplan"]);

/** Which panes start expanded when auto-collapse is OFF — the dock as it was before the option existed. */
export const STACKED_SECTIONS_OPEN = Object.freeze({ sheets: true, keyplan: true, queue: true, review: true, rooms: true, conditions: true, routes: false, layers: true });

/** The starting open/closed state for a layout: stacked defaults normally, the lean accordion set when auto-collapse is on. */
export const initialSectionsOpen = (layout) => ({ ...(layout && layout.autoCollapse ? DEFAULT_SECTIONS_OPEN : STACKED_SECTIONS_OPEN) });

/** Which panes start expanded with auto-collapse ON: just Conditions — the rest are one click away and then fill the column. */
export const DEFAULT_SECTIONS_OPEN = Object.freeze({ sheets: true, keyplan: false, queue: false, review: false, rooms: false, conditions: true, routes: false, layers: false });

/**
 * Click on a pane header. Opening a pane collapses the others in ITS column so it gets the room (a long Conditions or Rooms list
 * is the point of opening one); `keep` (Shift / Ctrl / Cmd held) leaves the others open. Closing a pane touches nothing else.
 * `columnKeys` = the pane keys sharing a column with `key`.
 */
export function toggleDockPane(open, key, columnKeys, { keep = false } = {}) {
  if (open[key]) return { ...open, [key]: false };
  const next = { ...open, [key]: true };
  if (!keep && !ACCORDION_EXEMPT.includes(key)) for (const k of columnKeys) if (k !== key && !ACCORDION_EXEMPT.includes(k)) next[k] = false;
  return next;
}

/** The visible panes, in order, as one column — or split into [left, right] when two columns are on. */
export function dockColumns(layout) {
  const visible = layout.order.filter(k => !layout.hidden.includes(k));
  if (!layout.twoCol) return [visible];
  return [visible.filter(k => layout.cols[k] !== 1), visible.filter(k => layout.cols[k] === 1)];
}

// ── Floating dock (feedback #34) ──────────────────────────────────────────────────────────────────────────────────────
// The ⇱ arrow beside the dock's ◂ button pops the whole dock out into a window the viewer can drag and resize over the
// drawing; ⇲ docks it back. Per viewer (localStorage), never estimate data — same rule as the layout above.
export const DOCK_FLOAT_STORAGE_KEY = "cs.takeoff.dockFloat";
export const DOCK_FLOAT_MIN_W = 240, DOCK_FLOAT_MIN_H = 200, DOCK_FLOAT_TITLE_H = 28;
/** How much of the window must stay on screen, so its title bar can always be grabbed again. */
const KEEP_VISIBLE_PX = 80;

/**
 * Whatever was stored (or nothing) + the browser window's size -> a complete floating-dock state, always reachable:
 * at least the minimum size, no bigger than the window, and its title bar on screen (a window saved on a bigger monitor
 * comes back inside this one). `fallback` = the size to start from when nothing was stored.
 */
export function normalizeDockFloat(raw, viewport, fallback = {}) {
  const src = raw && typeof raw === "object" ? raw : {};
  const vw = Math.max(320, Number(viewport?.w) || 1280), vh = Math.max(240, Number(viewport?.h) || 800);
  const num = (v, d) => (Number.isFinite(Number(v)) && v !== null && v !== "" ? Number(v) : d);
  const w = Math.round(Math.min(vw, Math.max(DOCK_FLOAT_MIN_W, num(src.w, num(fallback.w, DOCK_ONE_COL_W)))));
  const h = Math.round(Math.min(vh, Math.max(DOCK_FLOAT_MIN_H, num(src.h, num(fallback.h, Math.round(vh * 0.7))))));
  const x = Math.round(Math.min(vw - KEEP_VISIBLE_PX, Math.max(KEEP_VISIBLE_PX - w, num(src.x, vw - w - 24))));
  const y = Math.round(Math.min(vh - DOCK_FLOAT_TITLE_H, Math.max(0, num(src.y, 120))));
  return { floating: src.floating === true, x, y, w, h };
}

export function readDockFloat(viewport, fallback, storage = typeof localStorage !== "undefined" ? localStorage : null) {
  try { return normalizeDockFloat(JSON.parse(storage?.getItem(DOCK_FLOAT_STORAGE_KEY) || "null"), viewport, fallback); } catch { return normalizeDockFloat(null, viewport, fallback); }
}

export function writeDockFloat(state, storage = typeof localStorage !== "undefined" ? localStorage : null) {
  try { storage?.setItem(DOCK_FLOAT_STORAGE_KEY, JSON.stringify(state)); } catch { /* a view preference only */ }
}
